import { getExternalId } from '../../../../../external-sources/utils/externalIds';

/** API field names (snake_case and camelCase) for each external link. */
const LINK_KEYS = {
  wpSessionId: ['wp_session_id', 'wpSessionId'],
  faiconSessionId: ['faicon_session_id', 'faiconSessionId'],
  eventWpId: ['event_wp_id', 'eventWpId'],
  eventFaiconId: ['event_faicon_id', 'eventFaiconId'],
};
const LINK_FIELDS = Object.keys(LINK_KEYS);
export const EMPTY_LINKS = Object.fromEntries(LINK_FIELDS.map((field) => [field, '']));

/** Which link fields each picker tab edits. */
export const SOURCE_FIELDS = {
  WordPress: { event: 'eventWpId', session: 'wpSessionId' },
  Faicon: { event: 'eventFaiconId', session: 'faiconSessionId' },
};

export const savedLinksOf = (session) =>
  Object.fromEntries(LINK_FIELDS.map((field) => [field, getExternalId(session, ...LINK_KEYS[field])]));

export const sameLinks = (a, b) => LINK_FIELDS.every((field) => a[field] === b[field]);

const normalizeExternalId = (value) => String(value || '').trim().toLowerCase();

export const externalIdsMatch = (left, right) => (
  normalizeExternalId(left) !== ''
  && normalizeExternalId(left) === normalizeExternalId(right)
);

/** Draft links, with the active tab's event / session taken from the picker when it has them. */
export const linksToSave = (draft, linkSource, picker) => {
  const values = Object.fromEntries(LINK_FIELDS.map((field) => [field, draft[field].trim()]));
  const { event, session } = SOURCE_FIELDS[linkSource];
  values[event] = ((picker?.eventId?.trim() || '') || draft[event]).trim();
  values[session] = ((picker?.sessionId?.trim() || '') || draft[session]).trim();
  return values;
};

/** @returns {{ title: string, message: string } | null} why the links cannot be saved yet */
export const saveProblem = ({ values, linkSource, picker, pickerLoading, showManualIds }) => {
  const { event, session } = SOURCE_FIELDS[linkSource];
  if (pickerLoading || picker?.isLoading) {
    return { title: 'Still loading', message: 'Wait for events and sessions to finish loading before saving.' };
  }
  if (values[event] && !values[session]) {
    return {
      title: 'Session not selected',
      message: `Pick a day and session on the ${linkSource} tab. That session ID is used when publishing.`,
    };
  }
  if (linkSource === 'Faicon' && values.faiconSessionId) {
    if (values.eventFaiconId && externalIdsMatch(values.faiconSessionId, values.eventFaiconId)) {
      return {
        title: 'Wrong Faicon session',
        message: 'Session ID must differ from the event ID. Pick the session from the Day/Session dropdown.',
      };
    }
    if (!showManualIds && !picker?.hasSession) {
      return { title: 'Session not selected', message: 'Select a session from the Day/Session dropdown before saving.' };
    }
  }
  if (LINK_FIELDS.every((field) => !values[field])) {
    return { title: 'Nothing to save', message: 'Pick an event and session from the list or enter IDs manually.' };
  }
  return null;
};

export const savedMessage = ({ sessionUpdated, eventUpdated }) => {
  const parts = [sessionUpdated && 'session', eventUpdated && 'event'].filter(Boolean);
  return parts.length > 0
    ? `Saved ${parts.join(' and ')} link${parts.length > 1 ? 's' : ''}.`
    : 'External links were updated.';
};
